/**
 * Display model for the multiplier.
 *
 * It owns no math of its own: the curve comes from `@crashx/shared` so the
 * number the player reads is bit-for-bit the number the server paid. The model
 * only knows *which* t to feed it and when to stop.
 *
 * `valueAt()` runs on every frame from both the Pixi ticker and the rAF text
 * loop, so it allocates nothing and stays pure — the caller passes the server
 * time in, the model never reads a clock itself.
 */
import { GAME_DEFAULTS, elapsedForMultiplier, multiplierAt, multiplierRaw } from "@crashx/shared";

export interface MultiplierConfig {
  growthK?: number;
  maxMultiplier?: number;
}

export class MultiplierModel {
  private growthK: number;
  private maxMultiplier: number;
  private startedAt: number | null = null;
  private crashAt: number | null = null;

  constructor(config: MultiplierConfig = {}) {
    this.growthK = config.growthK ?? GAME_DEFAULTS.growthK;
    this.maxMultiplier = config.maxMultiplier ?? GAME_DEFAULTS.maxMultiplier;
  }

  /** Apply the public config from `state:sync` (the admin can change both live). */
  configure(config: MultiplierConfig): void {
    if (config.growthK !== undefined) this.growthK = config.growthK;
    if (config.maxMultiplier !== undefined) this.maxMultiplier = config.maxMultiplier;
  }

  /** `round:start` — `startedAt` is server time, not local time. */
  start(startedAt: number): void {
    this.startedAt = startedAt;
    this.crashAt = null;
  }

  /** `round:crash` — freeze on the authoritative value. */
  crash(crashAt: number): void {
    this.crashAt = crashAt;
  }

  /** Back to idle between rounds. */
  reset(): void {
    this.startedAt = null;
    this.crashAt = null;
  }

  get isFlying(): boolean {
    return this.startedAt !== null && this.crashAt === null;
  }

  get hasCrashed(): boolean {
    return this.crashAt !== null;
  }

  get startedAtMs(): number | null {
    return this.startedAt;
  }

  /** Elapsed flight time in ms at `serverNow`, clamped at 0. */
  elapsedAt(serverNow: number): number {
    if (this.startedAt === null) return 0;
    const elapsed = serverNow - this.startedAt;
    return elapsed > 0 ? elapsed : 0;
  }

  /**
   * Multiplier to display (2 decimals, floored) at `serverNow`.
   * Clamped by the crash value once known, and by the round ceiling otherwise —
   * a late `round:crash` must never let the UI show an impossible number.
   */
  valueAt(serverNow: number): number {
    if (this.startedAt === null) return 1;
    const value = multiplierAt(this.elapsedAt(serverNow), this.growthK);
    return this.clamp(value);
  }

  /** Unfloored value, for curve geometry and particle speed. */
  rawAt(serverNow: number): number {
    if (this.startedAt === null) return 1;
    const value = multiplierRaw(this.elapsedAt(serverNow), this.growthK);
    return this.clamp(value);
  }

  /** Milliseconds of flight needed to reach `multiplier` (auto-cashout previews). */
  timeToReach(multiplier: number): number {
    return elapsedForMultiplier(multiplier, this.growthK);
  }

  private clamp(value: number): number {
    const ceiling = this.crashAt ?? this.maxMultiplier;
    return value > ceiling ? ceiling : value;
  }
}
